import { Logger } from '~~/helpers/logger';

import { EXPONDO_MIDDLEWARE_ROUTES } from '~~/constants/routes';
import { OUT_OF_STOCK_ERROR } from '~~/constants/stock';
import type { Cart, InventoryEntry, LineItem } from '~~/types/commercetools';

type SkuType = InventoryEntry['sku'];
type InventoryData = {
  inventoryEntries: {
    results: InventoryEntry[]
  }
};
/*
 * Custom composable for fetching product stock from commercetools Inventory endpoint.
 */
export const useStock = (cachingKey: string) => {

  const { makeCommercetoolsRequest } = useExpondoMiddleware();
  const result: Ref<InventoryEntry[] | null> = useState(`useStock-${cachingKey}`, () => null);

  const loading: Ref<boolean> = useState(`useStock-loading-${cachingKey}`, () => false);

  const error: Ref<{search: null | string, inventory: null | string}> = useState(`useStock-error-${cachingKey}`, () =>
    ({
      search: null,
      inventory: null,
    }));

  const getStocks = async (params: { sku: SkuType | SkuType[], cartId?: Cart['id'] }) => {

    Logger.debug('inventory', params);

    try {

      loading.value = true;

      if (Array.isArray(params.sku) && params.sku.length === 0) {

        result.value = [];

      } else {

        const apiCallResult = await makeCommercetoolsRequest<InventoryData>({
          endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.GET_INVENTORY,
          data: params,
        });
        result.value = apiCallResult?.data?.inventoryEntries?.results || [];

      }
      error.value.search = null;

    } catch (err) {

      error.value.search = err ? String(err) : null;
      Logger.error('inventory', err);

    } finally {

      loading.value = false;

    }

  };

  const setStock = (value: InventoryEntry[]) => {

    result.value = value;

  };

  const findStockBySku = (sku: SkuType): InventoryEntry | undefined => {

    return result.value?.find((stock) =>
      stock.sku === sku,
    );

  };

  const findProductStockBySku = (sku: string): InventoryEntry['availableQuantity'] | 0 => {

    const stock = findStockBySku(sku);

    return stock?.availableQuantity || 0;

  };

  const loadStock = async ({
    items,
    cartId,
  }: { items: LineItem[], cartId?: Cart['id'] }) => {

    if (!items || items.length === 0) {

      return;

    }
    const skuList: string[] = items?.map((item: LineItem) => item?.variant?.sku ?? '').filter(sku => sku !== '');

    if (skuList.length) {

      await getStocks({
        sku: skuList,
        cartId,
      });

    }

  };

  const checkIfAllLineItemsHaveStock = async ({
    items,
    cartId,
  }: { items: LineItem[], cartId?: Cart['id'] }): Promise<void> => {

    await loadStock({ items, cartId });

    const allItemsHaveStock = items.every((item) => {

      const sku = item?.variant?.sku;

      return sku ? findProductStockBySku(sku) > 0 : false;

    });

    error.value.inventory = allItemsHaveStock ? null : OUT_OF_STOCK_ERROR;

  };

  return {
    getStocks,
    stocks: result,
    findStockBySku,
    findProductStockBySku,
    loadStock,
    checkIfAllLineItemsHaveStock,
    loading,
    error,
    setStock,
  };

};
